import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ProtectedRoute from './components/ProtectedRoute';
import CookieConsent from './components/CookieConsent';
import Login from './pages/Login';
import Cadastro from './pages/Cadastro';
import CadastroEstudante from './pages/CadastroEstudante';
import EsqueciSenha from './pages/EsqueciSenha';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LandingPage from './pages/LandingPage';
import ListaPacientes from './pages/ListaPacientes';
import FormPaciente from './pages/FormPaciente';
import DetalhesPaciente from './pages/DetalhesPaciente';
import ListaConsultas from './pages/ListaConsultas';
import FormConsulta from './pages/FormConsulta';
import Agenda from './pages/Agenda';
import Relatorios from './pages/Relatorios';
import SistemaNotificacoes from './components/SistemaNotificacoes';
import Dashboard from './pages/Dashboard';
import DashboardMock from './pages/DashboardMock';
import ProntuarioConsulta from './pages/ProntuarioConsulta';
import ListaUsuarios from './pages/ListaUsuarios';
import FormUsuario from './pages/FormUsuario';
import ImportExportPage from './pages/ImportExportPage';
import AlterarSenha from './components/AlterarSenha';
import PersonalizarPage from './pages/PersonalizarPage';
import Configuracoes from './pages/Configuracoes';
import TodoList from './pages/TodoList';
import PoliticaSeguranca from './pages/PoliticaSeguranca';
import SophyGames from './pages/SophyGames';
import SophyCacaPalavras from './pages/SophyCacaPalavras';
import SophyPalavrasCruzadas from './pages/SophyPalavrasCruzadas';
import Sophyteca from './pages/Sophyteca';
import SophytecaFavoritos from './pages/SophytecaFavoritos';
import SophytecaAdmin from './pages/SophytecaAdmin';
import LogsAuditoria from './pages/LogsAuditoria';
import AnaliseGeral from './pages/AnaliseGeral';
import VisualChecklist from './components/VisualChecklist';
import { PagamentoPage } from './pages/Pagamento';
import NotFound from './pages/NotFound';
import { authService } from './services/authService';
import SessionDebug, { useSessionDebug } from './components/SessionDebug';
import { PreferenciasProvider } from './contexts/PreferenciasContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { ForCargo } from './components/CanAccess';
import MobileMenuButton from './components/MobileMenuButton';
import NotificationBell from './components/NotificationBell';
import SidebarUserPhoto from './components/SidebarUserPhoto';
import { NavLink } from 'react-router-dom';

// ✅ ÁREA ADMINISTRATIVA
import { AdminAuthProvider, ProtectedAdminRoute } from './contexts/AdminAuthContext';
import { LoginAdmin, AdminDashboard, AdminEmpresas, AdminUsuarios, AdminAuditoria, AdminComunicacao } from './pages/admin';
import { AdminLayout } from './components/admin/AdminLayout';
import { ListaPesquisas, EditorPesquisa, EstatisticasPesquisa, RespostasPesquisa } from './pages/admin/pesquisas';

// ✅ PÁGINAS PÚBLICAS
import { RespostaPesquisa } from './pages/public/RespostaPesquisa';

function App() {
  const location = useLocation();
  const [showAlterarSenha, setShowAlterarSenha] = useState(false);
  // Controle explícito do estado de expansão do menu SophyGames
  const [sophyGamesOpen, setSophyGamesOpen] = useState<boolean>(false);
  
  // Hook para debug de sessão (Ctrl+Shift+D)
  const { showDebug } = useSessionDebug();
  
  // Corrigir a determinação de isAdmin para usar o método do serviço de autenticação
  const usuarioAtual = authService.getUsuario();
  const isAtendente = usuarioAtual?.cargo === 'atendente';
  // const isAdmin = authService.isAdmin(); // não utilizado diretamente aqui
  
  // IMPORTANTE: Declarar isPublicRoute ANTES de usar nos useEffect
  const isAuthenticated = authService.isAuthenticated();
  
  // Lista de rotas protegidas conhecidas
  const protectedRoutes = [
    '/dashboard', '/pacientes', '/consultas', '/agenda', '/relatorios',
    '/analise-geral', '/todo-list', '/sophygames', '/sophyteca',
    '/usuarios', '/import-export', '/personalizar', '/configuracoes',
    '/pagamento', '/logs-auditoria', '/visual-checklist'
  ];
  
  // Lista de rotas públicas conhecidas
  const publicRoutes = [
    '/login', '/cadastro', '/cadastro-estudante', 
    '/esqueci-senha', '/forgot-password', '/reset-password',
    '/dashboardmock', '/politica-seguranca',
    '/login-admin' // ✅ Login da área administrativa
  ];
  
  // Verificar se é rota da área administrativa
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname === '/login-admin';
  
  // Verificar se é rota de pesquisa pública
  const isPesquisaPublica = location.pathname.startsWith('/pesquisa/');
  
  // Determinar se a rota atual é pública
  const isKnownPublicRoute = publicRoutes.includes(location.pathname) || 
                              (location.pathname === '/' && !isAuthenticated) ||
                              isPesquisaPublica;
  const isKnownProtectedRoute = protectedRoutes.some(route => location.pathname.startsWith(route));
  
  // Se não está logado e não é uma rota protegida conhecida, trata como público (para mostrar 404 público)
  const isPublicRoute = isKnownPublicRoute || (!isAuthenticated && !isKnownProtectedRoute);
  
  // Diagnóstico SIMPLIFICADO para nova arquitetura
  useEffect(() => {
//     console.log('[APP] Inicialização do App com novo sistema cross-tab', {
//       url: window.location.href,
//       pathname: location.pathname,
//       isPublicRoute,
//       sessaoInfo: crossTabSession.getSessionInfo()
//     });
  }, [location.pathname, isPublicRoute]);

  // Aguardar inicialização do authService
  useEffect(() => {
    authService.waitForInitialization().then(() => {
//       console.log('[APP] AuthService inicializado');
    });
  }, []);

  // Inicializar AdminLTE treeview após login para garantir que submenus funcionem
  useEffect(() => {
    if (isAuthenticated && !isPublicRoute) {
      // Função para expandir menus
      const expandActiveMenu = () => {
        // Buscar todos os itens com submenu (has-treeview)
        const treeviewItems = document.querySelectorAll('.nav-item.has-treeview');
        
        treeviewItems.forEach((item: any) => {
          // Verificar se algum link dentro do submenu está ativo
          const activeLink = item.querySelector('.nav-treeview .nav-link.active');
          
          if (activeLink) {
            // Adicionar classe menu-open no item pai para expandir
            item.classList.add('menu-open');
            
            // Adicionar classe active no link pai (âncora do submenu)
            const parentLink = item.querySelector(':scope > .nav-link');
            if (parentLink) {
              parentLink.classList.add('active');
            }
          } else {
            // Remover classes se não há link ativo (para colapsar outros menus)
            item.classList.remove('menu-open');
            const parentLink = item.querySelector(':scope > .nav-link');
            if (parentLink) {
              parentLink.classList.remove('active');
            }
          }
        });
      };
      
  // Executar imediatamente
  expandActiveMenu();
  // Abrir/fechar SophyGames com base na rota atual
  setSophyGamesOpen(location.pathname.startsWith('/sophygames'));
      
      // Executar novamente após um pequeno delay (garante que o DOM está pronto)
      const timer1 = setTimeout(expandActiveMenu, 100);
      
      // Executar mais uma vez para garantir (para casos de renderização lenta)
      const timer2 = setTimeout(expandActiveMenu, 300);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isAuthenticated, isPublicRoute, location.pathname]);

  return (
    <PreferenciasProvider>
      <PermissionProvider>
        <div className="wrapper">
          {showDebug && <SessionDebug />}
        
        {/* Modal de alterar senha */}
        {showAlterarSenha && (
          <AlterarSenha onClose={() => setShowAlterarSenha(false)} />
        )}

        {/* ✅ ÁREA ADMINISTRATIVA - Sistema separado */}
        {isAdminRoute ? (
          <AdminAuthProvider>
            <Routes>
              {/* Login Admin */}
              <Route path="/login-admin" element={<LoginAdmin />} />
              
              {/* Rotas protegidas do Admin */}
              <Route path="/admin" element={<ProtectedAdminRoute><AdminLayout /></ProtectedAdminRoute>}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="empresas" element={<AdminEmpresas />} />
                <Route path="usuarios" element={<AdminUsuarios />} />
                <Route path="auditoria" element={<AdminAuditoria />} />
                <Route path="comunicacao" element={<AdminComunicacao />} />
                {/* Pesquisas/Formulários */}
                <Route path="pesquisas" element={<ListaPesquisas />} />
                <Route path="pesquisas/nova" element={<EditorPesquisa />} />
                <Route path="pesquisas/:id/editar" element={<EditorPesquisa />} />
                <Route path="pesquisas/:id/estatisticas" element={<EstatisticasPesquisa />} />
                <Route path="pesquisas/:id/respostas" element={<RespostasPesquisa />} />
                {/* Rotas futuras */}
                <Route path="administradores" element={<div className="text-center py-5"><h3>Em desenvolvimento</h3><p className="text-muted">Gerenciamento de administradores do sistema</p></div>} />
                <Route path="configuracoes" element={<div className="text-center py-5"><h3>Em desenvolvimento</h3><p className="text-muted">Configurações do sistema</p></div>} />
              </Route>
              
              {/* 404 para rotas admin não encontradas */}
              <Route path="/admin/*" element={<Navigate to="/admin/dashboard" replace />} />
            </Routes>
          </AdminAuthProvider>
        ) : isPublicRoute ? (
          // Rotas públicas (landing, login, cadastro, etc.)
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/cadastro" element={<Cadastro />} />
            <Route path="/cadastro-estudante" element={<CadastroEstudante />} />
            <Route path="/esqueci-senha" element={<EsqueciSenha />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboardmock" element={<DashboardMock />} />
            <Route path="/politica-seguranca" element={<PoliticaSeguranca />} />
            {/* Pesquisa pública - responder formulários */}
            <Route path="/pesquisa/:linkPublico" element={<RespostaPesquisa />} />
            {/* Rota catch-all para 404 (rotas públicas) */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        ) : (
          // Layout completo para rotas protegidas
          <>
            {/* Sistema de notificações - APENAS na área logada */}
          <SistemaNotificacoes />
          
          <aside className="main-sidebar sidebar-dark-primary elevation-4">
            <Link to={isAtendente ? '/consultas' : '/dashboard'} className="brand-link">
              <img 
                src="/img/logo-icone.png" 
                alt="Logo Sophya" 
                className="brand-image img-circle elevation-3" 
                style={{ opacity: 0.8 }} 
              />
              <span className="brand-text font-weight-light">Sophya</span>
            </Link>

            <div className="sidebar">
              <div className="user-panel mt-3 pb-3 mb-3 d-flex">
                <div className="image">
                  <SidebarUserPhoto />
                </div>
                <div className="info">
                  <Link to={isAtendente ? '/consultas' : '/dashboard'} className="d-block">
                    {usuarioAtual ? usuarioAtual.nome : 'Usuário'}
                  </Link>
                </div>
              </div>

              <nav className="mt-2">
                <ul className="nav nav-pills nav-sidebar flex-column" data-widget="treeview" role="menu" data-accordion="false">
                  {!isAtendente && (
                    <li className="nav-item">
                      <Link to="/dashboard" className={`nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-tachometer-alt"></i>
                        <p>Dashboard</p>
                      </Link>
                    </li>
                  )}
                  
                  <li className="nav-item">
                    <Link to="/pacientes" className={`nav-link ${location.pathname.startsWith('/pacientes') ? 'active' : ''}`}>
                      <i className="nav-icon fas fa-users"></i>
                      <p>Pacientes</p>
                    </Link>
                  </li>
                  
                  <li className="nav-item">
                    <Link to="/consultas" className={`nav-link ${location.pathname.startsWith('/consultas') ? 'active' : ''}`}>
                      <i className="nav-icon fas fa-calendar-check"></i>
                      <p>Consultas</p>
                    </Link>
                  </li>
                  
                  <li className="nav-item">
                    <Link to="/agenda" className={`nav-link ${location.pathname === '/agenda' ? 'active' : ''}`}>
                      <i className="nav-icon fas fa-calendar"></i>
                      <p>Agenda</p>
                    </Link>
                  </li>
                  
                  {!isAtendente && (
                    <li className="nav-item">
                      <Link to="/relatorios" className={`nav-link ${location.pathname === '/relatorios' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-chart-bar"></i>
                        <p>Relatórios</p>
                      </Link>
                    </li>
                  )}
                  
                  {!isAtendente && (
                    <li className="nav-item">
                      <Link to="/analise-geral" className={`nav-link ${location.pathname === '/analise-geral' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-brain"></i>
                        <p>Análise Geral (IA)</p>
                      </Link>
                    </li>
                  )}

                                    
                  {!isAtendente && (
                    <li className="nav-item">
                      <Link to="/todo-list" className={`nav-link ${location.pathname === '/todo-list' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-tasks"></i>
                        <p>Lista de Tarefas</p>
                      </Link>
                    </li>
                  )}
                  
                  {!isAtendente && (
                  <li 
                    id="sophygames-menu"
                    className={`nav-item has-treeview ${sophyGamesOpen ? 'menu-open' : ''}`}
                  >
                    <a 
                      href="#" 
                      className={`nav-link ${sophyGamesOpen ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setSophyGamesOpen((prev) => !prev);
                      }}
                    >
                      <i className="nav-icon fas fa-gamepad"></i>
                      <p>
                        SophyGames 🎮
                        <i className="right fas fa-angle-left"></i>
                      </p>
                    </a>
                    <ul className="nav nav-treeview" style={{ display: sophyGamesOpen ? 'block' : 'none' }}>
                      <li className="nav-item">
                        <Link to="/sophygames" className={`nav-link ${location.pathname === '/sophygames' ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon"></i>
                          <p>Flashcards</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link to="/sophygames/cacapalavras" className={`nav-link ${location.pathname === '/sophygames/cacapalavras' ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon"></i>
                          <p>Caça-Palavras</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link to="/sophygames/palavrascruzadas" className={`nav-link ${location.pathname === '/sophygames/palavrascruzadas' ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon"></i>
                          <p>Palavras Cruzadas</p>
                        </Link>
                      </li>
                    </ul>
                  </li>
                  )}
                  
                  {!isAtendente && (
                    <li className="nav-item">
                      <Link to="/sophyteca" className={`nav-link ${location.pathname.startsWith('/sophyteca') ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-book-reader"></i>
                        <p>Sophyteca 📚</p>
                      </Link>
                    </li>
                  )}

                  {/* Menu Administrativo - Apenas ADMIN */}
                  {/* Admin + Estudante têm acesso a Usuários e Import/Export */}
                  <ForCargo cargos={['admin','estudante']}>
                    <li className="nav-header">ADMINISTRAÇÃO</li>
                    <li className="nav-item">
                      <Link to="/usuarios" className={`nav-link ${location.pathname.startsWith('/usuarios') ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-user-cog"></i>
                        <p>Usuários</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link to="/import-export" className={`nav-link ${location.pathname === '/import-export' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-exchange-alt"></i>
                        <p>Import/Export</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link to="/logs-auditoria" className={`nav-link ${location.pathname === '/logs-auditoria' ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-clipboard-check"></i>
                        <p>Auditoria</p>
                      </Link>
                    </li>
                  </ForCargo>

                  {/* Acesso do atendente à lista de usuários (somente visualização) */}
                  <ForCargo cargos={['atendente']}>
                    <li className="nav-item">
                      <Link to="/usuarios" className={`nav-link ${location.pathname.startsWith('/usuarios') ? 'active' : ''}`}>
                        <i className="nav-icon fas fa-user"></i>
                        <p>Usuários</p>
                      </Link>
                    </li>
                  </ForCargo>

                  {!isAtendente && (
                    <>
                      <li className="nav-header">CONFIGURAÇÕES</li>
                      <li className="nav-item">
                        <Link to="/personalizar" className={`nav-link ${location.pathname === '/personalizar' ? 'active' : ''}`}>
                          <i className="nav-icon fas fa-palette"></i>
                          <p>Personalizar</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link to="/configuracoes" className={`nav-link ${location.pathname === '/configuracoes' ? 'active' : ''}`}>
                          <i className="nav-icon fas fa-cogs"></i>
                          <p>Configurações</p>
                        </Link>
                      </li>
                    </>
                  )}
                  
                  {/* Pagamento */}
                  <li className="nav-item">
                    <Link to="/pagamento" className="nav-link">
                      <i className="nav-icon fas fa-credit-card"></i>
                      <p>Pagamento</p>
                    </Link>
                  </li>
                  
                  <li className="nav-item">
                    <a 
                      href="#" 
                      className="nav-link"
                      onClick={(e) => {
                        e.preventDefault();
                        if (confirm('Tem certeza que deseja sair?')) {
                          authService.logout();
                        }
                      }}
                    >
                      <i className="nav-icon fas fa-sign-out-alt"></i>
                      <p>Sair</p>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </aside>

          <nav className="main-header navbar navbar-expand navbar-white navbar-light">
            <ul className="navbar-nav">
              <li className="nav-item">
                <a className="nav-link" data-widget="pushmenu" href="#" role="button">
                  <i className="fas fa-bars"></i>
                </a>
              </li>
            </ul>

            <ul className="navbar-nav ml-auto">
              {/* Sino de notificações */}
              <NotificationBell />
              
              <li className="nav-item dropdown">
                <a className="nav-link" data-toggle="dropdown" href="#">
                  <i className="far fa-user"></i>
                  <span className="ml-2">{usuarioAtual ? usuarioAtual.nome : 'Usuário'}</span>
                </a>
                <div className="dropdown-menu dropdown-menu-lg dropdown-menu-right">
                  <div className="dropdown-divider"></div>
                  <a 
                    href="#" 
                    className="dropdown-item"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowAlterarSenha(true);
                    }}
                  >
                    <i className="fas fa-key mr-2"></i> Alterar Senha
                  </a>
                  <div className="dropdown-divider"></div>
                  <a 
                    href="#" 
                    className="dropdown-item"
                    onClick={(e) => {
                      e.preventDefault();
                      if (confirm('Tem certeza que deseja sair?')) {
                        authService.logout();
                      }
                    }}
                  >
                    <i className="fas fa-sign-out-alt mr-2"></i> Sair
                  </a>
                </div>
              </li>
            </ul>
          </nav>

          {/* Overlay para sidebar em mobile */}
          <div className="sidebar-overlay d-md-none" />

          {/* Botão flutuante de menu apenas em mobile (opcional) */}
          <MobileMenuButton />

          <div className="content-wrapper">
            <Routes>
              {/* Rotas protegidas */}
              <Route path="/" element={<ProtectedRoute><Navigate to={isAtendente ? '/consultas' : '/dashboard'} replace /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <Dashboard />}</ProtectedRoute>} />
              
              {/* Pacientes */}
              <Route path="/pacientes" element={<ProtectedRoute><ListaPacientes /></ProtectedRoute>} />
              <Route path="/pacientes/novo" element={<ProtectedRoute><FormPaciente /></ProtectedRoute>} />
              <Route path="/pacientes/:id/editar" element={<ProtectedRoute><FormPaciente /></ProtectedRoute>} />
              <Route path="/pacientes/:id" element={<ProtectedRoute><DetalhesPaciente /></ProtectedRoute>} />
              <Route path="/pacientes/:id/prontuario" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <ProntuarioConsulta />}</ProtectedRoute>} />
              
              {/* Consultas */}
              <Route path="/consultas" element={<ProtectedRoute><ListaConsultas /></ProtectedRoute>} />
              <Route path="/consultas/nova" element={<ProtectedRoute><FormConsulta /></ProtectedRoute>} />
              <Route path="/consultas/:id/editar" element={<ProtectedRoute><FormConsulta /></ProtectedRoute>} />
              <Route path="/consultas/:id/prontuario" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <ProntuarioConsulta />}</ProtectedRoute>} />
              
              {/* Agenda */}
              <Route path="/agenda" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
              
              {/* Relatórios */}
              <Route path="/relatorios" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <Relatorios />}</ProtectedRoute>} />
              
              {/* Análise Geral de Paciente */}
              <Route path="/analise-geral" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <AnaliseGeral />}</ProtectedRoute>} />
              
              {/* To-Do List */}
              <Route path="/todo-list" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <TodoList />}</ProtectedRoute>} />
              
              {/* SophyGames */}
              <Route path="/sophygames" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <SophyGames />}</ProtectedRoute>} />
              <Route path="/sophygames/cacapalavras" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <SophyCacaPalavras />}</ProtectedRoute>} />
              <Route path="/sophygames/palavrascruzadas" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <SophyPalavrasCruzadas />}</ProtectedRoute>} />
              
              {/* Sophyteca - Biblioteca Digital */}
              <Route path="/sophyteca" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <Sophyteca />}</ProtectedRoute>} />
              <Route path="/sophyteca/favoritos" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <SophytecaFavoritos />}</ProtectedRoute>} />
              <Route path="/sophyteca/admin" element={<ProtectedRoute admin><SophytecaAdmin /></ProtectedRoute>} />
              
              {/* Rotas administrativas */}
              {/* Lista de usuários: admin, atendente (visualização) e estudante (visualização + editar próprio) */}
              <Route path="/usuarios" element={<ProtectedRoute><ForCargo cargos={['admin','atendente','estudante']} fallback={<Navigate to="/consultas" replace /> }><ListaUsuarios /></ForCargo></ProtectedRoute>} />
              <Route path="/usuarios/novo" element={<ProtectedRoute admin><FormUsuario /></ProtectedRoute>} />
              {/* Editar usuário: admin qualquer; estudante apenas próprio (backend reforça) */}
              <Route path="/usuarios/:id/editar" element={<ProtectedRoute><ForCargo cargos={['admin','estudante']} fallback={<Navigate to="/dashboard" replace /> }><FormUsuario /></ForCargo></ProtectedRoute>} />
              
              {/* Import/Export */}
              {/* Import/Export permitido para admin e estudante */}
              <Route path="/import-export" element={<ProtectedRoute><ForCargo cargos={['admin','estudante']} fallback={<Navigate to="/dashboard" replace /> }><ImportExportPage /></ForCargo></ProtectedRoute>} />
              
              {/* Personalização */}
              <Route path="/personalizar" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <PersonalizarPage />}</ProtectedRoute>} />
              
              {/* Configurações */}
              <Route path="/configuracoes" element={<ProtectedRoute>{isAtendente ? <Navigate to="/consultas" replace /> : <Configuracoes />}</ProtectedRoute>} />
              {/* Pagamento */}
              <Route path="/pagamento" element={<ProtectedRoute><PagamentoPage /></ProtectedRoute>} />
              {/* Auditoria */}
              <Route path="/logs-auditoria" element={<ProtectedRoute><ForCargo cargos={['admin','estudante']} fallback={<Navigate to="/dashboard" replace /> }><LogsAuditoria /></ForCargo></ProtectedRoute>} />
              {/* Visual Checklist (apenas admin/estudante) */}
              <Route path="/visual-checklist" element={<ProtectedRoute><ForCargo cargos={['admin','estudante']} fallback={<Navigate to="/dashboard" replace /> }><VisualChecklist /></ForCargo></ProtectedRoute>} />
              {/* Rota catch-all para 404 (rotas protegidas) */}
              <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
            </Routes>
          </div>

          {/* Barra de navegação rápida mobile (bottom) */}
          <div className="mobile-quick-nav d-md-none">
            <NavLink to={isAtendente ? '/consultas' : '/dashboard'} className={({isActive}) => isActive ? 'active' : ''}>
              <span className="icon fas fa-home" />
              <span>Início</span>
            </NavLink>
            <NavLink to="/pacientes" className={({isActive}) => isActive ? 'active' : ''}>
              <span className="icon fas fa-users" />
              <span>Pacientes</span>
            </NavLink>
            <NavLink to="/consultas" className={({isActive}) => isActive ? 'active' : ''}>
              <span className="icon fas fa-calendar-check" />
              <span>Consultas</span>
            </NavLink>
            <NavLink to="/agenda" className={({isActive}) => isActive ? 'active' : ''}>
              <span className="icon fas fa-calendar" />
              <span>Agenda</span>
            </NavLink>
            {!isAtendente && (
              <NavLink to="/sophygames" className={({isActive}) => isActive ? 'active' : ''}>
                <span className="icon fas fa-gamepad" />
                <span>Jogos</span>
              </NavLink>
            )}
          </div>

          <footer className="main-footer">
            <strong>Copyright &copy; 2024 <a href="#">Sophya</a>.</strong>
            Todos os direitos reservados.
            <div className="float-right d-none d-sm-inline-block">
              <b>Versão</b> 1.0.0
            </div>
          </footer>
        </>
      )}
          
          {/* Banner de Consentimento de Cookies (LGPD) */}
          <CookieConsent />
        </div>
      </PermissionProvider>
    </PreferenciasProvider>
  );
}

export default App;
